import { createRouter } from "@tanstack/react-router";
import { RouteError } from "@/components/shared/route-error";
import { Route as rootRoute } from "./__root";
import { Route as authenticatedRoute } from "./_authenticated";
import { Route as campusesRoute } from "./_authenticated/campuses";
import { Route as clientJourneyRoute } from "./_authenticated/client-journey";
import { Route as consultantsDivisionRoute } from "./_authenticated/consultants.division.$division";
import { Route as consultantsSummaryRoute } from "./_authenticated/consultants.summary";
import { Route as consultantsTerritoriesRoute } from "./_authenticated/consultants.territories";
import { Route as consultantsTerritoryRoute } from "./_authenticated/consultants.territory.$territory";
import { Route as dataSyncRoute } from "./_authenticated/data-sources";
import { Route as dataSyncDealsRoute } from "./_authenticated/data-sync-deals";
import { Route as dataSyncOwnersRoute } from "./_authenticated/data-sync-owners";
import { Route as dataSyncPipelinesRoute } from "./_authenticated/data-sync-pipelines";
import { Route as dataSyncShopifyOrdersRoute } from "./_authenticated/data-sync-shopify-orders";
import { Route as dataSyncTicketsRoute } from "./_authenticated/data-sync-tickets";
import { Route as dealMetricsRoute } from "./_authenticated/deal-metrics";
import { Route as dealMetricsDetailRoute } from "./_authenticated/deal-metrics-detail";
import { Route as dealValuesRoute } from "./_authenticated/deal-values.$field";
import { Route as dealsRoute } from "./_authenticated/deals";
import { Route as executiveRoute } from "./_authenticated/executive";
import { Route as executiveDrillRoute } from "./_authenticated/executive-drill.$metric.$month";
import { Route as expensesRoute } from "./_authenticated/expenses";
import { Route as hrRoute } from "./_authenticated/hr";
import { Route as hrOnboardingRoute } from "./_authenticated/hr.onboarding";
import { Route as hrRecruitmentRoute } from "./_authenticated/hr.recruitment";
import { Route as hrSuccessionPlanningRoute } from "./_authenticated/hr.succession-planning";
import { Route as marketingBenchmarksRoute } from "./_authenticated/marketing.benchmarks";
import { Route as marketingPaidRoute } from "./_authenticated/marketing.paid.$brand";
import { Route as marketingSocialRoute } from "./_authenticated/marketing.social.$brand.$platform";
import { Route as marketingSummaryRoute } from "./_authenticated/marketing.summary";
import { Route as marketingWebRoute } from "./_authenticated/marketing.web";
import { Route as metricsRoute } from "./_authenticated/metrics";
import { Route as myDashboardRoute } from "./_authenticated/my-dashboard";
import { Route as ownerDetailRoute } from "./_authenticated/owner-detail.$ownerId";
import { Route as ownerMetricsRoute } from "./_authenticated/owner-metrics.$ownerId";
import { Route as ownersRoute } from "./_authenticated/owners";
import { Route as settingsRoute } from "./_authenticated/settings";
import { Route as shopifyOrdersRoute } from "./_authenticated/shopify-orders";
import { Route as targetsRoute } from "./_authenticated/targets";
import { Route as teamsCreatedDevelopmentRoute } from "./_authenticated/teams.created.development";
import { Route as teamsCreatedMarketingRoute } from "./_authenticated/teams.created.marketing";
import { Route as teamsCreatedOperationsRoute } from "./_authenticated/teams.created.operations";
import { Route as teamsCreatedSalesRoute } from "./_authenticated/teams.created.sales";
import { Route as teamsFpConsultantsRoute } from "./_authenticated/teams.fp.consultants";
import { Route as teamsFpConsultantsNonRelationalRoute } from "./_authenticated/teams.fp.consultants.non-relational";
import { Route as teamsFpConsultantsRelationalRoute } from "./_authenticated/teams.fp.consultants.relational";
import { Route as teamsFpConsultantsWeddingRingRoute } from "./_authenticated/teams.fp.consultants.wedding-ring";
import { Route as teamsFpDevelopmentRoute } from "./_authenticated/teams.fp.development";
import { Route as teamsFpMarketingRoute } from "./_authenticated/teams.fp.marketing";
import { Route as teamsFpOperationsRoute } from "./_authenticated/teams.fp.operations";
import { Route as teamsFpmRoute } from "./_authenticated/teams.fpm";
import { Route as teamsFpmDevelopmentRoute } from "./_authenticated/teams.fpm.development";
import { Route as teamsFpmOperationRoute } from "./_authenticated/teams.fpm.operation";
import { Route as teamsFpmProductionRoute } from "./_authenticated/teams.fpm.production";
import { Route as territoriesRoute } from "./_authenticated/territories";
import { Route as usersRoute } from "./_authenticated/users";
import { Route as authenticatedFullBleedRoute } from "./_authenticatedFullBleed";
import { Route as databaseBrowserRoute } from "./_authenticatedFullBleed/database-browser";
import { Route as orgChartRoute } from "./_authenticatedFullBleed/org-chart";
import { Route as loginRoute } from "./login";

const routeTree = rootRoute.addChildren([
  loginRoute,
  authenticatedRoute.addChildren([
    campusesRoute,
    dataSyncRoute,
    dataSyncDealsRoute,
    dataSyncOwnersRoute,
    dataSyncPipelinesRoute,
    dataSyncShopifyOrdersRoute,
    dataSyncTicketsRoute,
    dealMetricsRoute,
    dealMetricsDetailRoute,
    dealValuesRoute,
    dealsRoute,
    executiveRoute,
    executiveDrillRoute,
    expensesRoute,
    clientJourneyRoute,
    hrRoute,
    hrOnboardingRoute,
    hrRecruitmentRoute,
    hrSuccessionPlanningRoute,
    metricsRoute,
    myDashboardRoute,
    ownerDetailRoute,
    ownerMetricsRoute,
    ownersRoute,
    targetsRoute,
    territoriesRoute,
    teamsCreatedDevelopmentRoute,
    teamsCreatedMarketingRoute,
    teamsCreatedOperationsRoute,
    teamsCreatedSalesRoute,
    teamsFpConsultantsRoute,
    teamsFpConsultantsNonRelationalRoute,
    teamsFpConsultantsRelationalRoute,
    teamsFpConsultantsWeddingRingRoute,
    teamsFpDevelopmentRoute,
    teamsFpMarketingRoute,
    marketingSummaryRoute,
    consultantsSummaryRoute,
    consultantsTerritoriesRoute,
    consultantsTerritoryRoute,
    consultantsDivisionRoute,
    marketingSocialRoute,
    marketingPaidRoute,
    marketingWebRoute,
    marketingBenchmarksRoute,
    teamsFpOperationsRoute,
    teamsFpmRoute,
    teamsFpmDevelopmentRoute,
    teamsFpmOperationRoute,
    teamsFpmProductionRoute,
    usersRoute,
    shopifyOrdersRoute,
    settingsRoute,
  ]),
  authenticatedFullBleedRoute.addChildren([databaseBrowserRoute, orgChartRoute]),
]);

export const router = createRouter({
  routeTree,
  defaultPreload: "intent",
  defaultPreloadDelay: 50,
  defaultErrorComponent: ({ error }) => <RouteError error={error} />,
});

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}
